import { Component, Input, OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import {
  AttachmentService,
  ExpenseApprovalSettings,
  ExpenseApprovalStatus,
  expenseApprovalStatusOptions,
  methods,
  selectAllExpenseCategories,
  selectExpenseSettings,
  selectExpenseUserApproveItem,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { createAutomationDateForm } from '../../date-form/automation-date-form.component'
import * as lodash from 'lodash'
import { select, Store } from '@ngrx/store'
import { AutomationDateRuleTypes, AutomationEventTypes, isValidAutomationDatePair } from '@tokeet-frontend/automations'
import { AuthService } from '@tv3/services/auth.service'
import { distinctUntilChanged } from 'rxjs/operators'
import { AutomationAttachmentsDataBaseComponent } from '@tv3/containers/automations/automation-data-form/automation-attachments-data-base.component'

export function createAutomationExpenseDataForm(fb: FormBuilder) {
  return fb.group({
    category: ['', [Validators.required]],
    amount: [undefined, [Validators.required]],
    date: createAutomationDateForm(fb, null, {
      baseRequired: true,
      daysRequired: true,
      hoursRequired: false,
    }),
    due: createAutomationDateForm(fb, null, {
      baseRequired: false,
      daysRequired: false,
      hoursRequired: false,
    }),
    method: ['', [Validators.required]],
    description: [],
    attachments: [[]],
    inquiry_id: [],
    approval_status: [ExpenseApprovalStatus.Pending],
  })
}

@Component({
  selector: 'app-automation-expense-data-form',
  templateUrl: './automation-expense-data-form.component.html',
  styleUrls: ['./automation-expense-data-form.component.scss'],
})
export class AutomationExpenseDataFormComponent extends AutomationAttachmentsDataBaseComponent implements OnInit {
  @Input() form: FormGroup
  @Input() eventCtrl: FormControl
  @Input() isEdit = false

  categories$ = this.store.pipe(select(selectAllExpenseCategories))
  methodOptions = methods

  approvalStatuses = expenseApprovalStatusOptions
  approvable$ = this.store.pipe(select(selectExpenseUserApproveItem(this.authService.user.id)))
  isAdmin = this.authService.isAdmin()

  expenseSettings: ExpenseApprovalSettings

  get attachmentsCtrl(): FormControl {
    return this.form.get('attachments') as FormControl
  }

  constructor(
    protected authService: AuthService,
    protected store: Store<any>,
    protected attachmentService: AttachmentService
  ) {
    super(store, authService, attachmentService)
  }

  ngOnInit(): void {
    super.ngOnInit()

    this.store.pipe(select(selectExpenseSettings), untilDestroy(this)).subscribe((s) => {
      this.expenseSettings = s
    })
    const startCtrl = this.form.get('date') as FormGroup
    const endCtrl = this.form.get('due') as FormGroup

    this.eventCtrl?.valueChanges.pipe(untilDestroy(this)).subscribe((event) => {
      startCtrl.reset({ base: AutomationDateRuleTypes.AfterBookingCreated, days: 1, hours: 0 })
      endCtrl.reset({ base: AutomationDateRuleTypes.AfterBookingCreated, days: 2, hours: 0 })
    })

    startCtrl.addValidators([
      (c) => {
        const data = c.value
        if (!isValidAutomationDatePair(data, endCtrl.getRawValue())) {
          return { invalid: true }
        }
      },
    ])
    endCtrl.addValidators([
      (c) => {
        const data = c.value
        if (!isValidAutomationDatePair(startCtrl.getRawValue(), data)) {
          return { invalid: true }
        }
      },
    ])
    endCtrl.valueChanges
      .pipe(
        distinctUntilChanged((a, b) => lodash.isEqual(a, b)),
        untilDestroy(this)
      )
      .subscribe(() => {
        startCtrl.updateValueAndValidity()
      })
    startCtrl.valueChanges
      .pipe(
        distinctUntilChanged((a, b) => lodash.isEqual(a, b)),
        untilDestroy(this)
      )
      .subscribe(() => {
        endCtrl.updateValueAndValidity()
      })
  }

  isBookingConfirmedOnBookingCreatedEvent(event: AutomationEventTypes, base: AutomationDateRuleTypes) {
    return (
      [AutomationEventTypes.InquiryCreated, AutomationEventTypes.InvoiceCreated].includes(event) &&
      base === AutomationDateRuleTypes.AfterBookingConfirmed
    )
  }

  getFormData() {
    const data = this.form.getRawValue()
    // if (!this.isEdit) {
    //   const autoApprove = data.amount <= this.expenseSettings?.auto_approve_amount
    //   data.approval_status = autoApprove ? ExpenseApprovalStatus.Approved : data.approval_status
    // }
    return data
  }
}
